/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { listOrdersData } from "../../modules/orders/core/_requests";
import { OrderProps } from "../../modules/orders/core/_models";
import socket from "../socket";

interface ChatProps {
  type: string;
  message: string;
  time: string;
  date: string;
  order_id: string;
  sender_type?: string;
}
interface ChefContextType {
  chefStatus: boolean;
  setChefStatus: React.Dispatch<React.SetStateAction<boolean>>;
  showOrderModal: boolean;
  setShowOrderModal: React.Dispatch<React.SetStateAction<boolean>>;
  orders: OrderProps[];
  setOrders: React.Dispatch<React.SetStateAction<OrderProps[]>>;
  messages: ChatProps[];
  setMessages: React.Dispatch<React.SetStateAction<ChatProps[]>>;
  order_id: any;
  senderType: any;
  receiverType: any;
  setSenderType: React.Dispatch<React.SetStateAction<string>>;
  setReceiverType: React.Dispatch<React.SetStateAction<string>>;
  setOrderId: React.Dispatch<React.SetStateAction<string>>;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChefContext = createContext<ChefContextType | undefined>(undefined);

export const useChef = (): ChefContextType => {
  const context = useContext(ChefContext);
  if (!context) {
    throw new Error("useChef must be used within a ChefProvider");
  }
  return context;
};
export interface DateTimeOptions {
  date?: Intl.DateTimeFormatOptions; // Options for date formatting
  time?: Intl.DateTimeFormatOptions; // Options for time formatting
}

export const formatDateTime = (
  dateString: string | Date, // Accepts both string and Date objects
  options: DateTimeOptions = {}
): { formattedDate: string; formattedTime: string } => {
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;

  // Default options for date and time formatting
  const defaultOptions: DateTimeOptions = {
    date: { month: "short", day: "2-digit", year: "numeric" }, // Mon DD, YYYY
    time: { hour: "2-digit", minute: "2-digit", hour12: true }, // HH:MM AM/PM
  };

  // Merge default options with user-provided options
  const dateOptions: Intl.DateTimeFormatOptions =
    options.date || defaultOptions.date!;
  const timeOptions: Intl.DateTimeFormatOptions =
    options.time || defaultOptions.time!;

  // Format date and time based on the options
  const formattedDate: string = new Intl.DateTimeFormat(
    "en-US",
    dateOptions
  ).format(date);
  const formattedTime: string = date.toLocaleTimeString("en-US", timeOptions);

  return { formattedDate, formattedTime };
};

interface ChefProviderProps {
  children: ReactNode;
}

const ChefProvider: React.FC<ChefProviderProps> = ({ children }) => {
  const currentUser = localStorage.getItem("frayoChefPortal");
  const parsedUser = currentUser ? JSON.parse(currentUser) : null;
  const chefId = parsedUser?.uuid || "";
  const [chefStatus, setChefStatus] = useState<boolean>(false);
  const [order_id, setOrderId] = useState<string>("");
  const [receiverType, setReceiverType] = useState<string>("");
  const [senderType, setSenderType] = useState<string>("");
  const [showOrderModal, setShowOrderModal] = useState<boolean>(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [messages, setMessages] = useState<ChatProps[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!currentUser); // ✅ Track login status

  useEffect(() => {
    if (chefId) {
      listOrdersData(chefId).then((res) => {
        setOrders(res?.data);
      });
    }
  }, []);
console.log("Current User=================================================:", currentUser);
  useEffect(() => {
    if (currentUser) {
      setIsLoggedIn(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isLoggedIn) return; // ✅ Prevent connection if not logged in

    if (!socket.connected) {
      socket.connect(); // Ensure it's connected before emitting or listening to events
    }

    socket.on("connect", () => {
    });
console.log("Socket ID=================================================:", socket.id);
    socket.on("disconnect", () => {
    });

    socket.on(
      "currentlyAvailableStatus",
      (data: { chefId: string; status: boolean }) => {
      console.log("Received currentlyAvailableStatus:", data);
        if (data.chefId === chefId) {
          //  
          setChefStatus(data.status);
        }
      }
    );
console.log("Socket Listeners Set Up=================================================:");
    socket.on(
      "orderStatusChanged",
      (data: {
        orderId: string;
        orderstatusId: number;
        orderstatus: { id: number; name: string };
        trackStatus: string;
      }) => {
         // Log the received data

        setOrders((prevOrders) => {
           // Log current orders before update

          const updatedOrders = prevOrders.map((order) => {
            if (order.uuid === data.orderId) {
              
              return {
                ...order,
                orderstatusId: data.orderstatusId,
                orderstatus: data.orderstatus,
                trackStatus: data.trackStatus,
              };
            } else {
              
            }
            return order;
          });

           // Log the updated orders after update
          return updatedOrders;
        });
      }
    );

    socket.on("newChatMessage", (data: ChatProps) => {
       // Log the received data

      setMessages((prevMessages) => {
         // Log current messages before update

        const { formattedDate, formattedTime } = formatDateTime(new Date());
        // Create the new message object
        const newMessage = {
          type: data.sender_type === "chef" ? "outgoing" : "incoming", // Determine message type
          message: data.message,
          time: formattedDate, // Generate current time
          date: formattedTime, // Generate current date
          order_id: data.order_id,
          sender_type: data.sender_type, // Include sender type
        };
        // Return the updated message list
        const updatedMessages = [...prevMessages, newMessage];
        return updatedMessages;
      });
    });

    socket.on("newOrder", (data: { data: any }) => {
       // Log the received data

      // Extract `data` properly if it's nested inside a key
      const newData = data?.data ? data.data : data;
       // Log the extracted newData

      setOrders((prevOrders) => {
         // Log current orders before update

        // Check if the order exists in the current list
        const orderExists = prevOrders.some(
          (order) => order.uuid === newData.uuid
        );

        let updatedOrders;

        if (orderExists) {
          updatedOrders = prevOrders.map((order) => {
            if (order.uuid === newData.uuid) {
              
              return { ...order, ...newData };
            }
            return order;
          });
        } else {
          updatedOrders = [...prevOrders, newData]; // ✅ Properly add `newData` as a single object to the array
        }

        setShowOrderModal(true);
         // Log the updated orders after update
        return updatedOrders;
      });
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [isLoggedIn]);

  return (
    <ChefContext.Provider
      value={{
        chefStatus,
        setChefStatus,
        orders,
        setOrders,
        showOrderModal,
        setShowOrderModal,
        messages,
        setMessages,
        receiverType,
        senderType,
        order_id,
        setOrderId,
        setReceiverType,
        setSenderType,
        setIsLoggedIn,
      }}
    >
      {children}
    </ChefContext.Provider>
  );
};

export default ChefProvider;
